import { useLoggedInEmployee } from "@/contexts/LoggedInEmployeeContext";
import { isE2EMode } from "@/lib/e2e";
import React from "react";
import { AccessError } from "./AccessError";
import { NoAccess } from "./NoAccess";

/**
 * Second gate, rendered inside LoggedInEmployeeProvider (which fetches the employee by
 * Clerk email). The Clerk auth gate in App.tsx ensures the user is signed in; this gate
 * additionally requires the matching employee to be an invited app user (is_user === true).
 *
 * NOTE: this is a client-side UX gate, not a security boundary. Server-side enforcement of
 * is_user is handed off to the upcoming RLS ticket (CAP2-151).
 */
export function AccessGate({ children }: { children: React.ReactNode }) {
  const { employee, loading, error, refresh } = useLoggedInEmployee();

  // E2E runs have no Clerk session (and thus no logged-in employee), so the access
  // check is bypassed there just like the Clerk-gated UI in App.tsx.
  if (isE2EMode()) {
    return <>{children}</>;
  }

  // Avoid flashing the NoAccess screen before the employee record resolves.
  if (loading) {
    return (
      <main className="access-screen">
        <div className="board-loading">Loading…</div>
      </main>
    );
  }

  // A lookup failure is recoverable and shouldn't be conflated with "not invited" — show an
  // explicit error with a retry rather than falling through to NoAccess.
  if (error) {
    return <AccessError onRetry={refresh} />;
  }

  if (!employee || !employee.is_user) {
    return <NoAccess />;
  }

  return <>{children}</>;
}
